import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Save, X, Upload, User, Mail, Phone, Linkedin, Instagram, Maximize2, Minimize2 } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useTranslation } from 'react-i18next';
import { getAssetUrl } from '../../utils/assetUrl';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import RichTextContent from '../../components/RichTextContent';
import I18nEquipeFields from '../../components/Admin/I18nEquipeFields';
import { emptyEquipeI18n, EquipeI18nBlock } from '../../i18n/localizeContent';
import { ActionButton, Button, IconButton } from '../../components/UI';

interface MembroEquipe {
  _id?: string;
  nome: string;
  cargo: string;
  descricao?: string;
  foto?: string;
  email?: string;
  telefone?: string;
  linkedin?: string;
  instagram?: string;
  ordem: number;
  ativo: boolean;
  especialidades?: string[];
  formacao?: Array<{
    titulo: string;
    instituicao: string;
    ano: number;
  }>;
  experiencia?: Array<{
    cargo: string;
    empresa: string;
    periodo: string;
    descricao: string;
  }>;
  idiomas?: Array<{
    idioma: string;
    nivel: string;
  }>;
  i18n?: EquipeI18nBlock;
  createdAt?: string;
  updatedAt?: string;
}

function equipeI18nFromMembro(membro: MembroEquipe): EquipeI18nBlock {
  return {
    pt: {
      nome: membro.i18n?.pt?.nome || membro.nome,
      cargo: membro.i18n?.pt?.cargo || membro.cargo,
      descricao: membro.i18n?.pt?.descricao ?? membro.descricao ?? ''
    },
    en: {
      nome: membro.i18n?.en?.nome || '',
      cargo: membro.i18n?.en?.cargo || '',
      descricao: membro.i18n?.en?.descricao || ''
    },
    es: {
      nome: membro.i18n?.es?.nome || '',
      cargo: membro.i18n?.es?.cargo || '',
      descricao: membro.i18n?.es?.descricao || ''
    }
  };
}

const AdminEquipe: React.FC = () => {
  const { t } = useTranslation();
  const [membros, setMembros] = useState<MembroEquipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMembro, setEditingMembro] = useState<MembroEquipe | null>(null);
  const { isExpanded: isModalExpanded, setIsExpanded: setIsModalExpanded, toggleExpanded: toggleModalExpanded } = useExpandable();
  useEscapeKey(() => closeModal(), showModal);
  const [formData, setFormData] = useState<MembroEquipe>({
    nome: '',
    cargo: '',
    descricao: '',
    i18n: emptyEquipeI18n(),
    foto: '',
    email: '',
    telefone: '',
    linkedin: '',
    ordem: 0,
    ativo: true,
    especialidades: [],
    formacao: [],
    experiencia: [],
    idiomas: []
  });
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const notifications = useNotifications();
  const confirm = useConfirm();

  const fetchMembros = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/equipe/admin');
      setMembros(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao buscar membros da equipe:', error);
      notifications.error.settingsUpdate('Erro ao carregar equipe');
      setMembros([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notifications estável em runtime; incluir nas deps causa loop de requisições
  }, []);

  useEffect(() => {
    fetchMembros();
  }, [fetchMembros]);

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('especialidades.')) {
      const index = parseInt(field.split('.')[1]);
      const newEspecialidades = [...(formData.especialidades || [])];
      newEspecialidades[index] = value;
      setFormData({ ...formData, especialidades: newEspecialidades });
    } else if (field.startsWith('formacao.')) {
      const [, indexStr, subField] = field.split('.');
      const index = parseInt(indexStr);
      const newFormacao = [...(formData.formacao || [])];
      if (!newFormacao[index]) newFormacao[index] = { titulo: '', instituicao: '', ano: 0 };
      (newFormacao[index] as any)[subField] = value;
      setFormData({ ...formData, formacao: newFormacao });
    } else if (field.startsWith('experiencia.')) {
      const [, indexStr, subField] = field.split('.');
      const index = parseInt(indexStr);
      const newExperiencia = [...(formData.experiencia || [])];
      if (!newExperiencia[index]) newExperiencia[index] = { cargo: '', empresa: '', periodo: '', descricao: '' };
      (newExperiencia[index] as any)[subField] = value;
      setFormData({ ...formData, experiencia: newExperiencia });
    } else if (field.startsWith('idiomas.')) {
      const [, indexStr, subField] = field.split('.');
      const index = parseInt(indexStr);
      const newIdiomas = [...(formData.idiomas || [])];
      if (!newIdiomas[index]) newIdiomas[index] = { idioma: '', nivel: '' };
      (newIdiomas[index] as any)[subField] = value;
      setFormData({ ...formData, idiomas: newIdiomas });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleFotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notifications.error.settingsUpdate('Apenas arquivos de imagem são permitidos');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notifications.error.settingsUpdate('Arquivo muito grande. Máximo 5MB');
      return;
    }

    try {
      setUploadingFoto(true);
      const formData = new FormData();
      formData.append('foto', file);

      const response = await axios.post('/equipe/upload-foto', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fotoUrl = response.data.fotoUrl;
      setFormData(prev => ({ ...prev, foto: fotoUrl }));
      setFotoPreview(fotoUrl);
      notifications.success.settingsUpdated();
    } catch (error: any) {
      console.error('Erro ao fazer upload da foto:', error);
      notifications.error.settingsUpdate('Erro ao fazer upload da foto');
    } finally {
      setUploadingFoto(false);
    }
  };

  const removeFoto = () => {
    setFormData(prev => ({ ...prev, foto: '' }));
    setFotoPreview(null);
  };

  const openModal = (membro?: MembroEquipe) => {
    if (membro) {
      const i18n = equipeI18nFromMembro(membro);
      setEditingMembro(membro);
      setFormData({
        ...membro,
        i18n,
        nome: i18n.pt?.nome || membro.nome,
        cargo: i18n.pt?.cargo || membro.cargo,
        descricao: i18n.pt?.descricao ?? membro.descricao ?? ''
      });
      setFotoPreview(membro.foto || null);
    } else {
      setEditingMembro(null);
      setFormData({
        nome: '',
        cargo: '',
        descricao: '',
        i18n: emptyEquipeI18n(),
        foto: '',
        email: '',
        telefone: '',
        linkedin: '',
        ordem: membros.length,
        ativo: true,
        especialidades: [],
        formacao: [],
        experiencia: [],
        idiomas: []
      });
      setFotoPreview(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingMembro(null);
    setIsModalExpanded(false);
    setFormData({
      nome: '',
      cargo: '',
      descricao: '',
      i18n: emptyEquipeI18n(),
      foto: '',
      email: '',
      telefone: '',
      linkedin: '',
      ordem: 0,
      ativo: true,
      especialidades: [],
      formacao: [],
      experiencia: [],
      idiomas: []
    });
    setFotoPreview(null);
  };

  const handleSave = async () => {
    const ptNome = formData.i18n?.pt?.nome?.trim();
    const ptCargo = formData.i18n?.pt?.cargo?.trim();
    if (!ptNome || !ptCargo) {
      notifications.error.settingsUpdate('Nome e cargo em português são obrigatórios.');
      return;
    }

    const payload: MembroEquipe = {
      ...formData,
      nome: ptNome,
      cargo: ptCargo,
      descricao: formData.i18n?.pt?.descricao ?? '',
      i18n: formData.i18n
    };

    try {
      setSaving(true);
      
      if (editingMembro) {
        await axios.put(`/equipe/${editingMembro._id}`, payload);
        notifications.success.settingsUpdated();
      } else {
        await axios.post('/equipe', payload);
        notifications.success.settingsUpdated();
      }
      
      fetchMembros();
      closeModal();
    } catch (error: any) {
      console.error('Erro ao salvar membro da equipe:', error);
      notifications.error.settingsUpdate('Erro ao salvar membro da equipe');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Excluir membro', message: 'Este membro será removido da equipe e do site.', confirmText: 'Excluir', variant: 'danger' }))) return;

    try {
      await axios.delete(`/equipe/${id}`);
      notifications.success.settingsUpdated();
      fetchMembros();
    } catch (error: any) {
      console.error('Erro ao deletar membro da equipe:', error);
      notifications.error.settingsUpdate('Erro ao deletar membro da equipe');
    }
  };

  const toggleAtivo = async (id: string, ativo: boolean) => {
    try {
      await axios.put(`/equipe/${id}`, { ativo: !ativo });
      notifications.success.settingsUpdated();
      fetchMembros();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      notifications.error.settingsUpdate('Erro ao alterar status');
    }
  };

  const addArrayItem = (field: string) => {
    if (field === 'especialidades') {
      setFormData(prev => ({ 
        ...prev, 
        especialidades: [...(prev.especialidades || []), ''] 
      }));
    } else if (field === 'formacao') {
      setFormData(prev => ({ 
        ...prev, 
        formacao: [...(prev.formacao || []), { titulo: '', instituicao: '', ano: 0 }] 
      }));
    } else if (field === 'experiencia') {
      setFormData(prev => ({ 
        ...prev, 
        experiencia: [...(prev.experiencia || []), { cargo: '', empresa: '', periodo: '', descricao: '' }] 
      }));
    } else if (field === 'idiomas') {
      setFormData(prev => ({ 
        ...prev, 
        idiomas: [...(prev.idiomas || []), { idioma: '', nivel: '' }] 
      }));
    }
  };

  const removeArrayItem = (field: string, index: number) => {
    if (field === 'especialidades') {
      const newEspecialidades = [...(formData.especialidades || [])];
      newEspecialidades.splice(index, 1);
      setFormData(prev => ({ ...prev, especialidades: newEspecialidades }));
    } else if (field === 'formacao') {
      const newFormacao = [...(formData.formacao || [])];
      newFormacao.splice(index, 1);
      setFormData(prev => ({ ...prev, formacao: newFormacao }));
    } else if (field === 'experiencia') {
      const newExperiencia = [...(formData.experiencia || [])];
      newExperiencia.splice(index, 1);
      setFormData(prev => ({ ...prev, experiencia: newExperiencia }));
    } else if (field === 'idiomas') {
      const newIdiomas = [...(formData.idiomas || [])];
      newIdiomas.splice(index, 1);
      setFormData(prev => ({ ...prev, idiomas: newIdiomas }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Breadcrumbs />
        </div>

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.team')}</h1>
              <p className="text-gray-600">Gerencie os membros da equipe</p>
            </div>
            <Button
              onClick={() => openModal()}>
              <Plus className="w-5 h-5" />
              <span>Adicionar Membro</span>
            </Button>
          </div>

        </div>

        {/* Estado de carregamento: só mostra loader, sem lista nem empty state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Carregando membros da equipe...</p>
          </div>
        )}

        {/* Lista de Membros (só quando não está carregando) */}
        {!loading && membros.length > 0 && (
        <div
          className={
            membros.length === 1
              ? 'flex justify-center'
              : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          }
        >
          {membros.map((membro) => {
            const solo = membros.length === 1;
            return (
            <div
              key={membro._id}
              className={`bg-white rounded-lg shadow-md overflow-hidden ${
                solo ? 'w-full max-w-3xl' : ''
              }`}
            >
              <div className={solo ? 'p-8' : 'p-6'}>
                <div
                  className={`mb-4 ${
                    solo
                      ? 'flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6 text-center sm:text-left'
                      : 'flex items-center space-x-4'
                  }`}
                >
                  {membro.foto ? (
                    <img
                      src={getAssetUrl(membro.foto)}
                      alt={membro.nome}
                      className={`rounded-full object-cover flex-shrink-0 ${
                        solo ? 'w-28 h-28 sm:w-32 sm:h-32' : 'w-16 h-16'
                      }`}
                    />
                  ) : (
                    <div
                      className={`bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 ${
                        solo ? 'w-28 h-28 sm:w-32 sm:h-32' : 'w-16 h-16'
                      }`}
                    >
                      <User className={solo ? 'w-12 h-12 text-gray-400' : 'w-8 h-8 text-gray-400'} />
                    </div>
                  )}
                  <div className={solo ? 'flex-1 min-w-0' : 'flex-1'}>
                    <h3
                      className={`font-semibold text-gray-900 ${
                        solo ? 'text-2xl' : 'text-lg'
                      }`}
                    >
                      {membro.nome}
                    </h3>
                    <p className={`text-gray-600 ${solo ? 'text-base mt-1' : 'text-sm'}`}>
                      {membro.cargo}
                    </p>
                    <div
                      className={`flex items-center space-x-2 mt-1 flex-wrap gap-y-1 ${
                        solo ? 'justify-center sm:justify-start' : ''
                      }`}
                    >
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        membro.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {membro.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      <span className="text-xs text-gray-500">Ordem: {membro.ordem}</span>
                    </div>

                    {membro.descricao && (
                      <RichTextContent
                        html={membro.descricao}
                        className={`text-gray-600 mt-3 ${solo ? 'text-base' : 'text-sm'}`}
                      />
                    )}
                  </div>
                </div>

                <div
                  className={`flex flex-wrap gap-2 mb-4 ${solo ? 'justify-center sm:justify-start' : ''}`}
                >
                  {membro.email && (
                    <a href={`mailto:${membro.email}`} className="flex items-center space-x-1 text-blue-600 hover:text-blue-800">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">Email</span>
                    </a>
                  )}
                  {membro.telefone && (
                    <a href={`tel:${membro.telefone}`} className="flex items-center space-x-1 text-green-600 hover:text-green-800">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">Telefone</span>
                    </a>
                  )}
                  {membro.linkedin && (
                    <a href={membro.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-blue-700 hover:text-blue-900">
                      <Linkedin className="w-4 h-4" />
                      <span className="text-sm">LinkedIn</span>
                    </a>
                  )}
                  {membro.instagram && (
                    <a href={membro.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-pink-600 hover:text-pink-800">
                      <Instagram className="w-4 h-4" />
                      <span className="text-sm">Instagram</span>
                    </a>
                  )}
                </div>

                <div className="row-actions md:justify-end">
                  <ActionButton
                    action={membro.ativo ? 'deactivate' : 'activate'}
                    onClick={() => toggleAtivo(membro._id!, membro.ativo)}
                  />
                  <ActionButton
                    action="edit"
                    onClick={() => openModal(membro)}
                  />
                  <ActionButton
                    action="delete"
                    onClick={() => handleDelete(membro._id!)}
                  />
                </div>
              </div>
            </div>
            );
          })}
        </div>
        )}

        {!loading && membros.length === 0 && (
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum membro da equipe</h3>
            <p className="text-gray-600 mb-4">Comece adicionando o primeiro membro da equipe.</p>
            <Button className="mx-auto"
              onClick={() => openModal()}>
              <Plus className="w-5 h-5" />
              <span>Adicionar Membro</span>
            </Button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto transition-all duration-200 ${
            isModalExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-4xl max-h-[90vh]'
          }`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingMembro ? 'Editar Membro da Equipe' : 'Adicionar Membro da Equipe'}
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={toggleModalExpanded}
                    className="icon-btn p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title={isModalExpanded ? 'Restaurar' : 'Expandir'}
                  >
                    {isModalExpanded ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
                  </button>
                  <IconButton action="close" onClick={closeModal} iconClassName="w-6 h-6" />
                </div>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
                {/* Informações Básicas */}
                <div className="border-b pb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Informações Básicas</h4>
                  <p className="text-sm text-gray-500 mb-4">{t('admin.teamI18nHint')}</p>

                  <I18nEquipeFields
                    value={formData.i18n || emptyEquipeI18n()}
                    onChange={(i18n) => setFormData((prev) => ({ ...prev, i18n }))}
                  />

                  {/* Foto */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto
                    </label>
                    
                    {fotoPreview && (
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={getAssetUrl(fotoPreview)}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={removeFoto}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center space-x-1 transition-colors duration-200"
                        >
                          <X className="w-4 h-4" />
                          <span>Remover</span>
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFotoUpload}
                          className="hidden"
                          disabled={uploadingFoto}
                        />
                        <div className={`btn-primary inline-flex items-center gap-2 ${uploadingFoto ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          {uploadingFoto ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Enviando...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              <span>{fotoPreview ? 'Alterar Foto' : 'Adicionar Foto'}</span>
                            </>
                          )}
                        </div>
                      </label>
                      
                      <div className="text-sm text-gray-500">
                        <p>Formatos: JPG, PNG, GIF</p>
                        <p>Tamanho máximo: 5MB</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contato */}
                <div className="border-b pb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Informações de Contato</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="field"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Telefone
                      </label>
                      <input
                        type="tel"
                        value={formData.telefone || ''}
                        onChange={(e) => handleInputChange('telefone', e.target.value)}
                        className="field"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      value={formData.linkedin || ''}
                      onChange={(e) => handleInputChange('linkedin', e.target.value)}
                      className="field"
                      placeholder="https://linkedin.com/in/usuario"
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instagram
                    </label>
                    <input
                      type="url"
                      value={formData.instagram || ''}
                      onChange={(e) => handleInputChange('instagram', e.target.value)}
                      className="field"
                      placeholder="https://instagram.com/usuario"
                    />
                  </div>
                </div>

                {/* Especialidades */}
                <div className="border-b pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Especialidades</h4>
                    <Button size="sm"
                      type="button"
                      onClick={() => addArrayItem('especialidades')}>
                      <Plus className="w-4 h-4" />
                      <span>Adicionar</span>
                    </Button>
                  </div>
                  
                  {formData.especialidades?.map((especialidade, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={especialidade}
                        onChange={(e) => handleInputChange(`especialidades.${index}`, e.target.value)}
                        className="field flex-1"
                        placeholder="Ex: Direito Tributário"
                      />
                      <IconButton action="remove" onClick={() => removeArrayItem('especialidades', index)} />
                    </div>
                  ))}
                </div>

                {/* Formação */}
                <div className="border-b pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Formação</h4>
                    <Button size="sm"
                      type="button"
                      onClick={() => addArrayItem('formacao')}>
                      <Plus className="w-4 h-4" />
                      <span>Adicionar</span>
                    </Button>
                  </div>
                  
                  {formData.formacao?.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Título
                          </label>
                          <input
                            type="text"
                            value={item.titulo}
                            onChange={(e) => handleInputChange(`formacao.${index}.titulo`, e.target.value)}
                            className="field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Instituição
                          </label>
                          <input
                            type="text"
                            value={item.instituicao}
                            onChange={(e) => handleInputChange(`formacao.${index}.instituicao`, e.target.value)}
                            className="field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ano
                          </label>
                          <input
                            type="number"
                            value={item.ano}
                            onChange={(e) => handleInputChange(`formacao.${index}.ano`, parseInt(e.target.value))}
                            className="field"
                          />
                        </div>
                      </div>
                      <IconButton action="remove" onClick={() => removeArrayItem('formacao', index)} className="mt-2" />
                    </div>
                  ))}
                </div>

                {/* Experiência */}
                <div className="border-b pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Experiência</h4>
                    <Button size="sm"
                      type="button"
                      onClick={() => addArrayItem('experiencia')}>
                      <Plus className="w-4 h-4" />
                      <span>Adicionar</span>
                    </Button>
                  </div>
                  
                  {formData.experiencia?.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cargo
                          </label>
                          <input
                            type="text"
                            value={item.cargo}
                            onChange={(e) => handleInputChange(`experiencia.${index}.cargo`, e.target.value)}
                            className="field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Empresa
                          </label>
                          <input
                            type="text"
                            value={item.empresa}
                            onChange={(e) => handleInputChange(`experiencia.${index}.empresa`, e.target.value)}
                            className="field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Período
                          </label>
                          <input
                            type="text"
                            value={item.periodo}
                            onChange={(e) => handleInputChange(`experiencia.${index}.periodo`, e.target.value)}
                            className="field"
                            placeholder="Ex: 2020 - 2023"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Descrição
                        </label>
                        <textarea
                          value={item.descricao}
                          onChange={(e) => handleInputChange(`experiencia.${index}.descricao`, e.target.value)}
                          rows={2}
                          className="field"
                        />
                      </div>
                      <IconButton action="remove" onClick={() => removeArrayItem('experiencia', index)} />
                    </div>
                  ))}
                </div>

                {/* Idiomas */}
                <div className="pb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">Idiomas</h4>
                    <Button size="sm"
                      type="button"
                      onClick={() => addArrayItem('idiomas')}>
                      <Plus className="w-4 h-4" />
                      <span>Adicionar</span>
                    </Button>
                  </div>
                  
                  {formData.idiomas?.map((item, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={item.idioma}
                        onChange={(e) => handleInputChange(`idiomas.${index}.idioma`, e.target.value)}
                        className="field flex-1"
                        placeholder="Ex: Inglês"
                      />
                      <input
                        type="text"
                        value={item.nivel}
                        onChange={(e) => handleInputChange(`idiomas.${index}.nivel`, e.target.value)}
                        className="field flex-1"
                        placeholder="Ex: Fluente"
                      />
                      <IconButton action="remove" onClick={() => removeArrayItem('idiomas', index)} />
                    </div>
                  ))}
                </div>

                {/* Botões */}
                <div className="form-actions pt-6 border-t">
                  <Button variant="ghost" size="sm"
                    type="button"
                    onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{editingMembro ? 'Salvar Alterações' : 'Adicionar Membro'}</span>
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEquipe;
